import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import SubscriptionAPI from "../../../api/subscription/SubscriptionApi";
import {
  NotificationVariant,
  useNotifications,
} from "../../../components/dashboard/NotificationsProvider";
import { useOrganization } from "../../facility/hooks/useOrganization";
import useFullPageLoader from "../../../hooks/useFullPageLoader";
import { getBillingOrgAuthId } from "../../../utils/getBillingOrgAuthId";

const useSubscription = () => {
  const subscriptionAPI = useMemo(() => new SubscriptionAPI(), []);
  const { organization } = useOrganization();
  const { setNotification } = useNotifications();
  const [, setLoading] = useFullPageLoader();
  const THIRTY_SECONDS_IN_MS = 30 * 1000;
  const orgId = getBillingOrgAuthId(organization);

  const {
    data: subscription,
    isLoading: isSubLoading,
    error: subError,
  } = useQuery({
    queryKey: ["subscription", orgId],
    queryFn: () => subscriptionAPI.getSubscription(orgId!),
    enabled: !!orgId,
    staleTime: THIRTY_SECONDS_IN_MS,
  });

  const {
    data: catalog,
    isLoading: isCatalogLoading,
    error: catalogError,
  } = useQuery({
    queryKey: ["catalog"],
    queryFn: () => subscriptionAPI.getCatalog(),
    staleTime: THIRTY_SECONDS_IN_MS * 10, // Catalog doesn't change often
  });

  const {
    data: paymentMethods,
    isLoading: isPMLoading,
    error: pmError,
  } = useQuery({
    queryKey: ["paymentMethods", orgId],
    queryFn: () => subscriptionAPI.getPaymentMethod(orgId!),
    enabled: !!orgId,
    staleTime: THIRTY_SECONDS_IN_MS,
  });

  const hasValidPaymentMethod = useMemo(() => {
    if (!paymentMethods || paymentMethods.length === 0) return false;
    const defaultPM = (paymentMethods as any[]).find((pm) => pm.isDefault);
    if (!defaultPM) return false;

    // Check expiry
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1; // getMonth() is 0-indexed

    if (defaultPM.expYear < currentYear) return false;
    if (defaultPM.expYear === currentYear && defaultPM.expMonth < currentMonth)
      return false;

    return true;
  }, [paymentMethods]);

  const isLoading = isSubLoading || isCatalogLoading || isPMLoading;

  useEffect(() => {
    if (orgId) {
      setLoading(isLoading);
    }
  }, [isLoading, setLoading, orgId]);

  useEffect(() => {
    const isMissingSubscription =
      subError && (subError as any).response?.status === 404;
    const isMissingPaymentMethod =
      pmError && (pmError as any).response?.status === 404;

    if (
      (subError && !isMissingSubscription) ||
      catalogError ||
      (pmError && !isMissingPaymentMethod)
    ) {
      console.error(
        `Subscription/PM fetch error: ${subError || catalogError || pmError}`,
      );
      setNotification({
        message: "An error occurred while fetching billing information",
        variant: NotificationVariant.error,
      });
    }
  }, [subError, catalogError, pmError, setNotification]);

  return {
    subscription: subscription || null,
    catalog: catalog || null,
    paymentMethods: paymentMethods || [],
    hasValidPaymentMethod,
    isLoading,
  };
};

export default useSubscription;
